/**
 * AttachmentStrip (DESIGN.md §12, T10): attachments of the open message under its header.
 * A click opens the attachment through `onOpen`: attached messages in the nested message view,
 * files as a download (the app decides). Without `onOpen` the items are listed only.
 *
 * Actions per attachment (DESIGN.md §18) come from the app's registry through `contextMenu`:
 * right-click, Shift+F10 and long-press on an attachment.
 */
import { Button } from '../components/button';
import { ContextMenuArea, type ContextMenuSpec } from '../actions/context-menu-area';
import { attachmentIcon } from './attachment-icon';

export interface AttachmentStripItem {
  id: string;
  name: string;
  /** file = file attachment, item = attached message. */
  kind: 'file' | 'item';
  /** Already formatted, e.g. "12 kB". */
  sizeLabel?: string;
}

export interface AttachmentStripProps {
  items: readonly AttachmentStripItem[];
  labels: {
    /** Accessible name of the list. */
    list: string;
    /** Accessible name of the open button of an attached message, contains `{name}`. */
    open: string;
    /** Accessible name of the button of a file, contains `{name}` (default: `open`). */
    download?: string;
  };
  /** Opens an attachment (attached message: nested view; file: download). */
  onOpen?: (id: string, kind: AttachmentStripItem['kind']) => void;
  /** Attachment actions (open, download …) for the context menu. */
  contextMenu?: (id: string) => ContextMenuSpec | undefined;
}

export function AttachmentStrip({ items, labels, onOpen, contextMenu }: AttachmentStripProps) {
  if (items.length === 0) return null;
  return (
    <ContextMenuArea menuFor={contextMenu}>
      <ul aria-label={labels.list} className="flex flex-wrap gap-2 border-b px-4 py-2">
        {items.map((item) => {
          const Icon = attachmentIcon(item.name, item.kind === 'item' ? 'message' : 'file');
          const content = (
            <>
              <Icon />
              <span className="max-w-60 truncate">{item.name}</span>
              {item.sizeLabel && <span className="text-xs text-muted-foreground">{item.sizeLabel}</span>}
            </>
          );
          const label = (item.kind === 'file' ? (labels.download ?? labels.open) : labels.open).replace('{name}', item.name);
          return (
            <li key={item.id} data-context-id={item.id}>
              {onOpen ? (
                <Button variant="outline" size="sm" aria-label={label} onClick={() => onOpen(item.id, item.kind)}>
                  {content}
                </Button>
              ) : (
                <span className="inline-flex h-control-sm items-center gap-2 rounded-md border px-3 text-sm font-medium [&_svg]:size-4 [&_svg]:text-muted-foreground">{content}</span>
              )}
            </li>
          );
        })}
      </ul>
    </ContextMenuArea>
  );
}
